import { useState } from 'react';

import { Field, controlClass } from '@/components/Field';
import { Button } from '@/components/PageHeading';
import { draftMoney, type BookingDraft } from '@core/bookings/draft';
import { refundPointLabel } from '@core/bookings/paymentWords';
import { depositMultiplierChoices } from '@core/bookings/takeBooking';
import { formatAmount } from '@core/format/money';
import { DepositRefundPointValues } from '@core/generated/enums';

import { Choice, Dialog, Group } from '../Dialog';

/**
 * When the deposit is taken and given back — the one money panel left behind a link; the figures
 * themselves are chosen on the bag (`PercentOrAmount`). Every figure is in **rupees first**,
 * with the multiple or percentage under it, because rupees are what a clerk says to the customer.
 * Each panel edits the draft as it is touched, so the bag behind is already right when it closes.
 */

type Patch = (changes: Partial<BookingDraft>) => void;

/** A typed figure, or null for an empty box — never NaN. */
const typed = (text: string): number | null => {
  const value = Number(text);

  return text.trim() === '' || !Number.isFinite(value) ? null : value;
};

/** "1½", "2", "3" — how a multiple is said. */
const multipleWord = (multiple: number) => (multiple === 1.5 ? '1½' : String(multiple));

function AmountInput({
  label,
  value,
  hint,
  onChange,
}: {
  label: string;
  value: number | null;
  hint?: string;
  onChange: (value: number | null) => void;
}) {
  return (
    <Field label={label} hint={hint}>
      <input
        type="number"
        min={0}
        autoFocus
        value={value ?? ''}
        onChange={(event) => onChange(typed(event.target.value))}
        onWheel={(event) => event.currentTarget.blur()}
        className={controlClass}
      />
    </Field>
  );
}

/* ------------------------------------------------------------------ deposit */

export function DepositDialog({
  draft,
  shopMultiplier,
  currency,
  patch,
  onClose,
}: {
  draft: BookingDraft;
  shopMultiplier: number;
  currency: string;
  patch: Patch;
  onClose: () => void;
}) {
  const cash = (amount: number) => `${currency}${formatAmount(amount)}`;
  const money = draftMoney(draft);
  const [other, setOther] = useState(draft.depositOverride !== null);
  const split = draft.depositCollectionPoint === 'Split';

  return (
    <Dialog
      title="Security deposit"
      subtitle={`Held, and given back after the return less any damage. The rental is ${cash(money.rental)}.`}
      onClose={onClose}
      footer={
        <Button variant="primary" shortcut="save" onClick={onClose} className="ms-auto">
          Done
        </Button>
      }
    >
      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
        {depositMultiplierChoices(shopMultiplier).map((multiple) => (
          <Choice
            key={multiple}
            label={cash(Math.round(money.rental * multiple))}
            caption={`${multiple === 1 ? 'Same as the rental' : `${multipleWord(multiple)} × the rental`}${
              multiple === shopMultiplier ? ' · usual' : ''
            }`}
            chosen={!other && draft.depositMultiplier === multiple}
            onChoose={() => {
              setOther(false);
              patch({ depositMultiplier: multiple, depositOverride: null });
            }}
          />
        ))}
        <Choice
          label="Other"
          caption="Type the amount"
          chosen={other}
          onChoose={() => setOther(true)}
        />
      </div>

      {other && (
        <>
          <AmountInput
            label="Deposit amount"
            value={draft.depositOverride}
            onChange={(depositOverride) => patch({ depositOverride })}
          />
          <Field
            label="Why it was changed"
            hint="Kept with the booking — the deposit-liability report reads it."
          >
            <input
              value={draft.depositOverrideReason}
              onChange={(event) => patch({ depositOverrideReason: event.target.value })}
              placeholder="Long-standing customer, five previous rentals"
              className={controlClass}
            />
          </Field>
        </>
      )}

      <Group label="When is it taken?">
        <div className="flex flex-wrap gap-1.5">
          <Choice
            label="At pickup"
            chosen={draft.depositCollectionPoint === 'AtPickup'}
            onChoose={() => patch({ depositCollectionPoint: 'AtPickup' })}
          />
          <Choice
            label="Now, with the token"
            chosen={draft.depositCollectionPoint === 'AtBooking'}
            onChoose={() => patch({ depositCollectionPoint: 'AtBooking' })}
          />
          <Choice
            label="Half now, half at pickup"
            chosen={split}
            onChoose={() => patch({ depositCollectionPoint: 'Split' })}
          />
        </div>
      </Group>

      <Group label="Given back">
        <div className="flex flex-wrap gap-1.5">
          {DepositRefundPointValues.map((point) => (
            <Choice
              key={point}
              label={refundPointLabel[point]}
              chosen={draft.depositRefundPoint === point}
              onChoose={() => patch({ depositRefundPoint: point })}
            />
          ))}
        </div>
      </Group>
    </Dialog>
  );
}
